/**
 * Client-side referral attribution.
 *
 * A visitor lands on /r/<code> (or any page with ?ref=<code>), the code is stored locally
 * with an expiry, and it survives the signup/login round trip. When they finally join a
 * mint space, joinMintSpace sends the code back so the backend can credit the affiliate.
 *
 * A code belongs to one product, but membership is space-wide, so the stored entry is
 * scoped to the *space* for the join call. The product it points at is recorded alongside
 * for display, and the backend reads it off the affiliate when the commission is paid.
 *
 * Local storage (not a cookie) because the API is on a different origin and the whole
 * attribution decision happens client-side anyway.
 */

const REF_KEY = "mintly_ref";
const VISITOR_KEY = "mintly_visitor_id";
const DEFAULT_WINDOW_DAYS = 30;

const safeParse = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Stable anonymous id so refreshing a referral link doesn't inflate the click count. */
export function getVisitorId() {
  if (typeof window === "undefined") return "";
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

/**
 * Remember a referral code. A newer code replaces an older one — last touch wins, which
 * matches what affiliates expect when a user clicks several links.
 */
export function storeReferral(
  code,
  { mintSpaceId = null, productId = null, windowDays = DEFAULT_WINDOW_DAYS } = {}
) {
  if (typeof window === "undefined" || !code) return null;
  const entry = {
    code: String(code).trim().toUpperCase(),
    mintSpaceId: mintSpaceId ? String(mintSpaceId) : null,
    productId: productId ? String(productId) : null,
    expiresAt: Date.now() + (windowDays || DEFAULT_WINDOW_DAYS) * 24 * 60 * 60 * 1000,
  };
  try {
    window.localStorage.setItem(REF_KEY, JSON.stringify(entry));
  } catch {
    return null;
  }
  return entry;
}

/** The stored referral, or null when absent or expired (expired entries are cleaned up). */
export function getStoredReferral() {
  if (typeof window === "undefined") return null;
  let entry;
  try {
    entry = safeParse(window.localStorage.getItem(REF_KEY));
  } catch {
    return null;
  }
  if (!entry?.code) return null;
  if (entry.expiresAt && entry.expiresAt < Date.now()) {
    clearReferral();
    return null;
  }
  return entry;
}

/**
 * The code to send with a join request for `mintSpaceId`.
 *
 * A code recorded for one space must not be spent on another, so an entry with a
 * mintSpaceId only applies to that space. Entries captured from a bare ?ref= (no space
 * resolved yet) are allowed through and validated server-side.
 */
export function getReferralCodeForSpace(mintSpaceId) {
  const entry = getStoredReferral();
  if (!entry) return "";
  if (entry.mintSpaceId && String(entry.mintSpaceId) !== String(mintSpaceId)) return "";
  return entry.code;
}

export function clearReferral() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(REF_KEY);
  } catch {
    /* storage disabled — nothing to clean up */
  }
}

/** Pull ?ref= off a URL's query string. */
export function readRefParam(search) {
  try {
    return new URLSearchParams(search || "").get("ref") || "";
  } catch {
    return "";
  }
}
